const {
    toSalesOrderRequest,
    toSubscriptionUpdateRequest,
    fromSalesOrderRequest,
    toSalesOrderResponse,
    fromSalesOrderResponse
} = require('../mapping/salesOrder');
const _ = require('lodash');

class Order {

    constructor(order) {
        // initialize nested objects
        this.lineItems = [];
        this.orderTotals = {};
        this.shipping = {
            shippingParties: [],
            shippingMethod: {}
        };
        this.customer = {};
        this.adr = {};
        this.messages = [];
        this.payments = [];
        this.reason = 'STANDARD';
        this.category = 'WEB';

        // update order with order data passed into the constructor
        if (order) {
            this.id = order.id;
            this.cartCreatedAt = order.cartCreatedAt;
            this.lastModifiedAt = order.lastModifiedAt;
            this.lineItems = order.lineItems && Order._mapLineItems(order.lineItems);
            this.orderTotals = Order._mapOrderTotals(order.orderTotals);
            this.type = order.type;
            if (order.category) this.category = order.category;
            this.channel = order.channel;
            this.shipping = order.shipping && Order._mapShipping(order.shipping);
            this.customer = order.customer && Order._mapCustomer(order.customer);
            if (order.action) this.action = order.action;
            this.soldToMemberId = order.soldToMemberId;
            this.salesOffice = order.salesOffice;
            this.orderId = order.orderId;
            this.externalId = order.externalId;
            this.adr = order.adr && Order._mapAdr(order.adr);
            this.countryCode = order.countryCode;
            this.date = order.date;
            this.dateCancelled = order.dateCancelled;
            this.dateReturned = order.dateReturned;
            this.productDiscountCode = order.productDiscountCode;
            this.shippingDiscountCode = order.shippingDiscountCode;
            this.commissionableInPriorMonth = order.commissionableInPriorMonth;
            this.commissionedInPriorMonth = order.commissionedInPriorMonth;
            if (order.reason) this.reason = order.reason;
            this.currencyCode = order.currencyCode;
            this.status = order.status;
            this.giftReceipt = order.giftReceipt;
            this.serialNumber = order.serialNumber;
            this.messages = order.messages && Order._mapMessages(order.messages);
            // This is the order timestamp
            this.timeStamp = order.timeStamp;
            this.orderProcessingState = order.orderProcessingState;
            this.redirectUrl = order.redirectUrl;
            this.payments = order.payments && Order._mapPayments(order.payments);
            this.pricing = order.pricing;
            // Custom fields mapped as primary attributes
            this.eventCode = order.eventCode;
            this.sender = order.sender;
            this.shippingPayWithPoints = order.shippingPayWithPoints;
            this.microsite = order.microsite;
        }
    }

    /**
     *  Set the ship method on the order
     * @param shipMethod
     * @return {Order}
     */
    setShipMethod(shipMethod) {
        if (!shipMethod) return this;
        const method = {
            code: shipMethod.code,
            default: shipMethod.default,
            description: shipMethod.description,
            cost: shipMethod.cost,
            pickUpLocation: shipMethod.pickUpLocation,
            currency: shipMethod.currency
        }
        this.shipping.shippingMethod = method;
        return this;
    }

    /**
     * Sync the line items in the order with the cart line items
     * @param {Object} cart
     * @return {Order}
     */
    syncLineItemsFromCart(cart) {
        if (!cart) return this;
        // Remove any line items that have been removed from the cart
        this.lineItems = this.lineItems.filter(
            (item) => !!cart.lineItems.find((li) => li.sku === item.sku && li.oneTime === item.oneTime)
        );

        cart.lineItems.forEach((lineItem) => {
            // update existing items quantity
            const existingItem = this.lineItems.find(
                (li) => li.sku === lineItem.sku && li.oneTime === lineItem.oneTime
            );
            if (existingItem) {
                existingItem.quantity = lineItem.quantity;
            } else {
                // todo handle agelocme and map other missing properties.
                // If the item doesn't exist add it to the line items array
                this.lineItems.push({
                    quantity: lineItem.quantity,
                    gpid: lineItem.gpid || lineItem.globalId,
                    sku: lineItem.sku,
                    oneTime: lineItem.oneTime,
                    isExclusive: lineItem.isExclusive,
                    title: lineItem.title
                });
            }
        });

        return this;
    }

    /**
     * Set the shipping party on the order using a shipping address from profile
     * @param {CREW.ShippingAddress} addr
     * @param {'SHIP_TO' | 'BILL_TO' | 'SOLD_TO' | 'PICK_UP'} [type]
     * @return {Order}
     */
    setShippingAddressFromProfile( addr, type = 'SHIP_TO') {
        if (!addr) return this;
        const shippingParty = {
            shippingMemberId: this.soldToMemberId,
            type,
            addressType: "home",
            shippingAddress: {
                name: addr.shippingAddressName,
                address: {
                    street1: addr.shippingAddress1,
                    street2: addr.shippingAddress2,
                    city: addr.shippingCity,
                    district: addr.shippingCounty,
                    region: addr.shippingState,
                    postalCode: addr.shippingPostalCode,
                    countryCode: addr.shippingCountry
                },
                careOfName: "",
                homePhone: addr.shippingPhone,
                phoneCountry: addr.shippingPhoneCountry,
                mobilePhone: addr.shippingMobilePhone,
                mobilePhoneCountry: addr.shippingMobilePhoneCountry,
                email: addr.shippingEmail,
                instructions: addr.shippingInstructions
            }
        }
        const partyIndex = this.shipping.shippingParties.findIndex(s => s.type === type);
        if (partyIndex >= 0) {
            this.shipping.shippingParties[partyIndex] = shippingParty;
        } else {
            this.shipping.shippingParties.push(shippingParty);
        }
        return this;
    }

    setPickUpShippingParty(pickUpLocation) {
        if (!pickUpLocation) return this;
        const shippingParty = {
            shippingMemberId: this.soldToMemberId,
            type: 'PICK_UP',
            pickupId: pickUpLocation.id,
            pickupLocker: pickUpLocation.locker,
            carrierCode: pickUpLocation.carrierCode,
            shippingAddress: {
                name: pickUpLocation.name,
                address: {
                    street1: pickUpLocation.street,
                    city: pickUpLocation.city,
                    postalCode: pickUpLocation.postalCode
                }
            }
        }
        const partyIndex = this.shipping.shippingParties.findIndex(s => s.type === shippingParty.type);
        if (partyIndex >= 0) {
            this.shipping.shippingParties[partyIndex] = shippingParty;
        } else {
            this.shipping.shippingParties.push(shippingParty);
        }
        return this;
    }

    /**
     *
     * @param pmnt
     * @param {number} otherInfo.installments
     * @param {boolean} otherInfo.sepaElectronic
     * @param {string} otherInfo.paymentOfsId - (bankKey | creditCard type | Wire custom field value)
     * @param {string} otherInfo.bankCountry
     * @param {string} otherInfo.verificationCode
     * @param otherInfo.type - paymentClass
     * @param otherInfo.accertifyTransactionId
     * @return {Order}
     */
    setPaymentFromProfile(pmnt, otherInfo) {
        if (!pmnt) return this;
        // If the payment doesn't have user information don't set the bePaymentTypeId.
        // Shop service throws an error if the user doesn't own the payment and crew has no record of it
        const setBePaymentTypeId = !!pmnt.paymentNumber;
        const payment = {
            type: otherInfo.type,
            ...(setBePaymentTypeId && {bePaymentTypeId: pmnt.bePaymentTypeId}),
            paymentTypeId: pmnt.paymentTypeId,
            currencyCode: this.currencyCode,
            action: 'ADD',
            installments: otherInfo.installments,
            paymentNumber: pmnt.ccToken || pmnt.paymentNumber,
            paymentName: pmnt.paymentName,
            ofsId: pmnt.paymentOfsId,
            bank: {
                key: pmnt.paymentOfsId,
                country: otherInfo.bankCountry,
                sepaElectronic: otherInfo.sepaElectronic
            },
            billingAddress: {
                address: pmnt.address1,
                address2: pmnt.address2,
                city: pmnt.city,
                region: pmnt.region,
                postalCode: pmnt.paymentPostalCode,
                district: pmnt.district,
                country: pmnt.country,
                phone: pmnt.phone,
                email: pmnt.email
            },
            storedCredentialsAgreementDate: pmnt.storedCredentialsAgreementDate,
            storedCredentialsNetworkTransactionId: pmnt.storedCredentialsNetworkTransactionId,
            storedCredentialsNetworkDateStr: pmnt.storedCredentialsNetworkDateStr
        }
        // Only add credit card if it exists
        if (pmnt.expMonth) {
            payment.creditCard = {
                type: pmnt.paymentOfsId,
                expirationMonth: pmnt.expMonth,
                expirationYear: pmnt.expYear,
                verificationCode: otherInfo.verificationCode,
                encryptedCardNumber: "",
                trackOne: '',
                accertifyTransactionId: otherInfo.accertifyTransactionId
            }
        }
        this.payments = [payment];
        return this;
    }

    toSalesOrderResponse() {
        return toSalesOrderResponse(this);
    }

    toSalesOrderRequest() {
        return toSalesOrderRequest(this);
    }

    toSubscriptionUpdateRequest() {
        return toSubscriptionUpdateRequest(this);
    }

    static fromSalesOrderRequest(salesOrderRequest) {
        if (!salesOrderRequest) {
            return;
        }
        const newOrder = fromSalesOrderRequest(salesOrderRequest);
        return new Order(newOrder);
    }

    static fromSalesOrderResponse(salesOrderResponse) {
        if (!salesOrderResponse) {
            return;
        }
        const newOrder = fromSalesOrderResponse(salesOrderResponse);
        return new Order(newOrder);
    }

    /**
     * Merges order2 into order1
     * @param {Object} order1 order from salesOrderRequest.  Line items will be overwritten
     * @param {Object} order2 order from SalesOrderResponse.  Contains exploded line items
     * @returns {Object} order
     */
    static merge(order1, order2) {
        if (!order1 && !order2) {
            return null;
        }

        if (order1 && !order2) {
            return order1;
        }

        if (order2 && !order1) {
            return order2;
        }

        return _.mergeWith({}, order1, order2, (obj, src) => {
            // handle merging of empty strings
            if (src === null || (typeof src === 'string' && !src.length)) {
                return obj;
            }
        });
    }

    /*** PRIVATE METHODS ***/
    static _mapPayments(payments) {
        if (payments == null) {
            return [];
        }

        return payments.map(payment => Order._mapPayment(payment));
    }

    static _mapPayment(payment) {
        if (payment == null) {
            return null;
        }

        return {
            type: payment.type,
            currencyCode: payment.currencyCode,
            action: payment.action,
            creditCard: Order._mapCreditCard(payment.creditCard),
            bank: Order._mapBank(payment.bank),
            billingAddress: Order._mapBillingAddress(payment.billingAddress),
            installments: payment.installments,
            paymentName: payment.paymentName,
            paymentNumber: payment.paymentNumber,
            storedCredentialsAgreementDate: payment.storedCredentialsAgreementDate,
            storedCredentialsNetworkTransactionId: payment.storedCredentialsNetworkTransactionId,
            storedCredentialsNetworkDateStr: payment.storedCredentialsNetworkDateStr,
            // Custom field mappings
            ...(typeof payment.paymentTypeId !== 'undefined' && {paymentTypeId: payment.paymentTypeId}),
            ...(payment.bePaymentTypeId && {bePaymentTypeId: payment.bePaymentTypeId})
        }
    }

    static _mapBillingAddress(billingAddress) {
        if (billingAddress == null) {
            return null;
        }

        // This is at the payment header leave in SAP. Need to figure out exact mappings.
        return {
            address: billingAddress.address,
            ...(billingAddress.district != null &&
                { district: billingAddress.district } ||
                (typeof(billingAddress.district) != "undefined" ? { district: null } : {})),
            city: billingAddress.city,
            region: billingAddress.region,
            postalCode: billingAddress.postalCode,
            ...(billingAddress.country != null &&
                { country: billingAddress.country } ||
                (typeof(billingAddress.country) != "undefined" ? { country: null } : {})),
            // custom field mappings
            ...(billingAddress.address2 && {address2: billingAddress.address2}),
            ...(billingAddress.phone && {phone: billingAddress.phone}),
            ...(billingAddress.email && {email: billingAddress.email})
        };
    }

    static _mapBank(bank) {
        if (!bank) {
            return null;
        }

        return {
            country: bank.country,
            account: bank.account,
            key: bank.key,
            sepaElectronic: bank.sepaElectronic
        };
    }

    static _mapCreditCard(creditCard) {
        if (!creditCard) {
            return null;
        }

        return {
            type: creditCard.type,
            expirationMonth: creditCard.expirationMonth,
            expirationYear: creditCard.expirationYear,
            verificationCode: creditCard.verificationCode,
            encryptedCardNumber: creditCard.encryptedCardNumber,
            trackOne: creditCard.trackOne,
            // Custom field mappings
            ...(creditCard.accertifyTransactionId && {accertifyTransactionId: creditCard.accertifyTransactionId}),
            ...(creditCard.cardType && {cardType: creditCard.cardType})
        }
    }

    static _mapTransactionType(transactionType) {
        if (transactionType == null) {
            return transactionType;
        }

        return {
            value: transactionType.value,
            paymentPeriod: transactionType.paymentPeriod
        };
    }

    static _mapAvailablePayment(availablePayment) {
        if (availablePayment == null) {
            return null;
        }

        return {
            id: availablePayment.id,
            ofsId: availablePayment.ofsId,
            blocked: !!availablePayment.blocked,
            processorCode: availablePayment.processorCode,
            formName: availablePayment.formName,
            paymentClass: availablePayment.paymentClass,
            ccImageUrl: availablePayment.ccImageUrl,
            merchantId: availablePayment.merchantId,
            localizedName: availablePayment.localizedName,
            installmentsAllowed: availablePayment.installmentsAllowed,
            installmentNumbers: availablePayment.installmentNumbers,
            minimumInstallmentAmount: availablePayment.minimumInstallmentAmount,
            simulateWhenNumberGT: availablePayment.simulateWhenNumberGT,
            binListInstallmentCheck: availablePayment.binListInstallmentCheck,
            disabled: availablePayment.disabled,
            cvvRequired: availablePayment.cvvRequired,
            needsTokenization: availablePayment.needsTokenization
        }
    }

    static _mapAdr(adr) {
        if (adr == null) {
            return null;
        }

        return {
            adrOverrideId: adr.adrOverrideId,
            //Custom field mappings
            adrContractLength: adr.adrContractLength
        }
    }

    static _mapMessages(messages) {
        if (messages == null) {
            return null;
        }

        return messages.map(msg => this._mapMessage(msg));
    }

    static _mapMessage(message) {
        if (message == null) {
            return null;
        }

        return {
            type: message.type,
            code: message.code,
            message: message.message,
            businessData: message.businessData,
            msgParameter1: message.msgParameter1,
            msgParameter2: message.msgParameter2,
            msgParameter3: message.msgParameter3,
            msgParameter4: message.msgParameter4
        }
    }

    static _mapShipping(shipping) {
        if (shipping == null) {
            return null;
        }

        return {
            shippingParties: shipping.shippingParties &&
                this._mapShippingParties(shipping.shippingParties) || [],
            shippingMethod: shipping.shippingMethod &&
                this._mapShippingMethod(shipping.shippingMethod),
            tracking: shipping.tracking,
            shippingAddress: shipping.shippingAddress
            // Todo add shipping service level
        };
    }

    static _mapShippingParties(shippingParties) {
        if (shippingParties == null) {
            return [];
        }

        return shippingParties.map(shippingParty => {
            return {
                shippingMemberId: shippingParty.shippingMemberId,
                type: shippingParty.type,
                shippingAddress: this._mapShippingPartyAddress(shippingParty.shippingAddress),
                ...(shippingParty.pickupId && {pickupId: shippingParty.pickupId}),
                ...(shippingParty.pickupLocker && {pickupLocker: shippingParty.pickupLocker}),
                ...(shippingParty.addressType && {addressType: shippingParty.addressType})
            };
        });
    }

    static _mapShippingPartyAddress(shippingAddress) {
        if (shippingAddress == null) {
            return null;
        }

        return {
            id: shippingAddress.id,
            name: shippingAddress.name,
            careOfName: shippingAddress.careOfName,
            address: Order._mapAddress(shippingAddress.address),
            homePhone: shippingAddress.homePhone,
            phoneCountry: shippingAddress.phoneCountry,
            mobilePhone: shippingAddress.mobilePhone,
            mobilePhoneCountry: shippingAddress.mobilePhoneCountry,
            email: shippingAddress.email,
            instructions: shippingAddress.instructions
        }
    }

    static _mapAddress(address) {
        if (address == null) {
            return null;
        }

        return {
            street1: address.street1,
            street2: address.street2,
            city: address.city,
            district: address.district,
            districtDesc: address.districtDesc,
            region: address.region,
            regionDesc: address.regionDesc,
            postalCode: address.postalCode,
            countryCode: address.countryCode,
            countryCodeDesc: address.countryCodeDesc,
            internationalAddresses: address.internationalAddresses
        };
    }

    static _mapShippingMethod(shippingMethod) {
        if (shippingMethod == null) {
            return null;
        }

        return {
            code: shippingMethod.code,
            default: shippingMethod.default,
            description: shippingMethod.description,
            cost: shippingMethod.cost,
            pickUpLocation: shippingMethod.pickUpLocation,
            currency: shippingMethod.currency
        };
    }

    static _mapCustomer(customer) {
        if (customer == null) {
            return null;
        }

        return {
            loyaltyTier: customer.loyaltyTier,
            customerInfoProcessed: customer.customerInfoProcessed,
            beType: customer.beType,
            isTemporary: customer.isTemporary,
            targetGroups: customer.targetGroups
        }
    }

    static _mapCreator(creator) {
        if (creator == null) {
            return null;
        }

        return {
            clientId: creator.clientId,
            isPlatformClient: creator.isPlatformClient
        };
    }

    static _mapLineItems(lineItems) {
        if (lineItems == null) {
            return null;
        }

        return lineItems.map(lineItem => {
            return {
                sku: lineItem.sku,

                // Mapped from custom fields
                ...(lineItem.agelocmeCode && {agelocmeCode: lineItem.agelocmeCode}),
                ...(lineItem.agelocmeName && {agelocmeName: lineItem.agelocmeName}),
                ...(lineItem.agelocmeLabel && {agelocmeLabel: lineItem.agelocmeLabel}),
                ...(lineItem.isExclusive && {isExclusive: lineItem.isExclusive}),
                ...(lineItem.gpid && {gpid: lineItem.gpid}),

                action: lineItem.action,
                id: lineItem.id,
                lineId: lineItem.lineId,
                parentId: lineItem.parentId,
                title: lineItem.title || lineItem.name,
                price: lineItem.price,
                points: lineItem.points,
                pointsUsed: lineItem.pointsUsed,
                rounding: lineItem.rounding,
                sharingBonus: lineItem.sharingBonus,
                tax: lineItem.tax,
                taxBase: lineItem.taxBase,
                taxInclusive: lineItem.taxInclusive,
                pricing: lineItem.pricing,
                quantity: lineItem.quantity,
                payWithPoints: lineItem.payWithPoints,
                oneTime: lineItem.oneTime,
                discountedPrice: lineItem.discountedPrice,
                discountedPricePerQuantity: lineItem.discountedPricePerQuantity,
                discountedPSV: lineItem.discountedPSV,
                discountedCV: lineItem.discountedCV,
                lineItemMode: lineItem.lineItemMode,
                addedBySystem: lineItem.addedBySystem,
                replacedSku: lineItem.replacedSku,
                availableQuantity: lineItem.availableQuantity,
                totalAvailableQuantity: lineItem.totalAvailableQuantity,
                freight: lineItem.freight,
                freightPromoSavings: lineItem.freightPromoSavings,
                productPromoSavings: lineItem.productPromoSavings,
                psv: lineItem.psv,
                cv: lineItem.cv,
                backOrderAvailableDate: lineItem.backOrderAvailableDate,
                backOrdered: lineItem.backOrdered,
                invalidSku: lineItem.invalidSku,
                grandFatheredPricing: lineItem.grandFatheredPricing,
                plant: lineItem.plant,
                storageLocation: lineItem.storageLocation
            };
        });
    }

    static _mapState(state) {
        if (state == null) {
            return null;
        }

        return state.map((s) => ({
            quantity: s.quantity,
            state: {
                typeId: s.typeId,
                id: s.id
            }
        }));
    }

    static _mapTaxRate(taxRate) {
        if (taxRate == null) {
            return null;
        }

        return {
            name: taxRate.name,
            amount: taxRate.amount,
            includedInPrice: taxRate.includedInPrice,
            country: taxRate.country,
            id: taxRate.id,
            subRates: taxRate.subRates
        }
    }

    static _mapAttributes(attribute) {
        if (attribute == null) {
            return null;
        }

        return {
            name: attribute.name,
            value: attribute.value
        };
    }

    static _mapOrderTotals(orderTotals) {
        if (orderTotals == null) {
            return null;
        }

        return {
            points: orderTotals.points,
            pointsUsed: orderTotals.pointsUsed,
            psv: orderTotals.psv,
            cv: orderTotals.cv,
            totalDiscountedCV: orderTotals.totalDiscountedCV,
            totalDiscountedPSV: orderTotals.totalDiscountedPSV,
            sharingBonus: orderTotals.sharingBonus,
            savings: orderTotals.savings,
            shipping: orderTotals.shipping,
            ...(orderTotals.freightPromoSavings != null &&
                { freightPromoSavings: orderTotals.freightPromoSavings } ||
                {}),
            ...(orderTotals.productPromoSavings != null &&
                { productPromoSavings: orderTotals.productPromoSavings } ||
                {}),
            taxBase: orderTotals.taxBase,
            rounding: orderTotals.rounding,
            taxInclusive: orderTotals.taxInclusive,
            grandTotal: orderTotals.grandTotal,
            itemSubtotal: orderTotals.itemSubtotal,
            tax: orderTotals.tax,

            //custom fields
            installmentFee: orderTotals.installmentFee,
            installmentTem: orderTotals.installmentTem,
            installmentTea: orderTotals.installmentTea,
            instalmentCft: orderTotals.installmentCft,
            suppTaxOne: orderTotals.suppTaxOne
        }
    }
}

module.exports = {
    Order: Order
};
